import showFullCompose from './showFullCompose';
import datapoints from '../../datapoints';
import friendlyEndDateForAllDay from 'owa-calendar-forms-common/lib/utils/friendlyEndDateForAllDay';
import { toJS } from 'mobx';
import { createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import { userInteractionAction } from 'owa-calendar-actions';
import { canEdit } from 'owa-calendar-event-capabilities';
import { fetchFullCalendarEventAndUpdateItemResponseShapeType } from 'owa-calendar-forms-common/lib/helpers/eventStore';
import initializeCalendarEvent from 'owa-calendar-forms-common/lib/utils/initializeCalendarEvent';
import { FullComposeEntrySourceType } from 'owa-calendar-helpers-types/lib/ComposeMetricTypes';
import { FullComposeFormViewType } from 'owa-calendar-helpers-types/lib/ComposeTypes';
import { CalendarEvent, initializeAsThisAndFollowingInstances } from 'owa-calendar-types';
import EventScope from 'owa-service/lib/contract/EventScope';
import {
    initializeCalendarEvents,
    handleComposePopoutForMeetingDraft,
} from 'owa-calendar-compose-form-actions';
import cloneDeep from 'lodash-es/cloneDeep';
import { copyCollabSpaceIfPresent } from '../../utils/collabSpaceComposeUtils';
import { isFeatureEnabled } from 'owa-feature-flags';

const EDIT_ENTRY_SOURCE = 'Edit';
/**
 * Gets called when we want to open the compose form in edit mode
 * @param item The item to open in compose (can be partial or a list of updated item properties on the item)
 * @param eventScope Scope of the event being edited
 * @param itemHasUpdates Indicates that the item in the first param contains updated properties that should be applied to the full item
 * @param fullComposeEntrySource The source type to use when opening the full compose form in edit mode, default is "Edit"
 * @param extractionSourceId A specific entity extraction id associates with the fullComposeEntrySource = Mail_SmartReplyWithMeeting
 * @param shouldInitializeForNewDraft Set by reply-by-meeting to handle online meetings and speedy meetings scenarios.
 * @param shouldRespectStartEndTime Indicates if the start time and end time should be respected (no speedy meetings applied)
 */
export default createLegacyActionWithDatapoint(
    'showFullComposeInEditMode',
    datapoints.showFullComposeInEditMode
)(async function showFullComposeInEditMode(
    item: Partial<CalendarEvent>,
    eventScope: EventScope,
    itemHasUpdates: boolean,
    fullComposeEntrySource: FullComposeEntrySourceType,
    viewType: FullComposeFormViewType,
    extractionSourceId: string | null,
    internetMessageId?: string,
    shouldInitializeForNewDraft?: boolean,
    shouldRespectStartEndTime?: boolean
): Promise<void> {
    let isInitializedEvent = false;

    if (!item) {
        // If partial calendar event is not passed, then load with defaults
        item = initializeCalendarEvent();
        isInitializedEvent = true;
    }

    // Log the user interaction action
    userInteractionAction('ViewOpened', 'EditForm', item.IsOrganizer, item.IsMeeting);

    let fullItem: CalendarEvent = null;

    if (item.ItemId?.Id) {
        // For existing events, retrieve the most up to date version from server.
        // Use fetchFullCalendarEventAndUpdateItemResponseShapeType with 'WithBlockedExternalContent'
        // to unblock external images since we are in a compose scenario.
        const { fullEventPromise } = fetchFullCalendarEventAndUpdateItemResponseShapeType(
            item.ItemId,
            'WithBlockedExternalContent',
            null,
            () =>
                // in most situations, we want to pass the actual item here, rather than a deep clone, because loading the full item from the
                // cache will overwrite the partial item's properties. However, in this case, we're passing the UPDATED item as the partial item
                // which we don't want the properties to be overwritten for because we'll lose those updates.
                cloneDeep(item) as CalendarEvent /* getExistingCalendarEvent */,
            eventScope === EventScope.ThisAndFollowingInstances ||
                eventScope === EventScope.AllInstancesInSeries,
            { fetchSource: 'FullComposeEdit' }
        );

        // We are creating a copy as we are mutating the object here so we dont want to mutate the store instance
        fullItem = toJS(await fullEventPromise);

        if (!canEdit(fullItem)) {
            // If you cannot edit the item, then return.
            // This might happen in the deeplink scenario where the user can pass any item id into showFullComposeInEditMode action.
            return;
        }
    } else {
        // For new events, set all defaults.
        fullItem = initializeCalendarEvent(item, isInitializedEvent, shouldRespectStartEndTime);
    }
    if (eventScope == EventScope.ThisAndFollowingInstances) {
        // If item does not have the necessary start/end properties, then retrieve the item first
        let instanceItem = item;
        if (!item.Start || !item.End) {
            instanceItem = fetchFullCalendarEventAndUpdateItemResponseShapeType(
                item.ItemId,
                'WithBlockedExternalContent',
                null /* folderId */,
                null /* getExistingCalendarEvent */,
                false /** isfetchFullSeriesMasterCalendarEventFromEventInstance */,
                { fetchSource: 'FullComposeInEditMode_' + fullComposeEntrySource }
            ).event;
        }

        // For editing this and following instances we need to make a hybrid item:
        fullItem = { ...fullItem };
        if (isFeatureEnabled('cal-cmp-fluidCollaborativeSpace') && fullItem.CollabSpace) {
            await copyCollabSpaceIfPresent(fullItem);
        }
        initializeAsThisAndFollowingInstances(fullItem, instanceItem as CalendarEvent);
    }
    if (fullItem.IsAllDayEvent) {
        fullItem.End = friendlyEndDateForAllDay(fullItem.End);
    }

    if (itemHasUpdates) {
        initializeCalendarEvents(fullItem, item);
    } else {
        initializeCalendarEvents(fullItem);
    }

    if (shouldInitializeForNewDraft) {
        handleComposePopoutForMeetingDraft();
    }

    showFullCompose(
        fullItem.CharmId != 0,
        fullComposeEntrySource || EDIT_ENTRY_SOURCE,
        viewType,
        extractionSourceId,
        internetMessageId
    );
});
